import { useState } from "react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { useScoreboard, bucketEvents, type ScoreEvent } from "@/hooks/useScoreboard";

function GameRow({ g }: { g: ScoreEvent }) {
  const isLive = g.status === "live";
  const isFinal = g.status === "final";
  return (
    <div className="flex items-center gap-4 rounded-xl border border-border bg-card p-4">
      <div className="w-20 shrink-0">
        <div className="text-[10px] font-semibold uppercase tracking-wider text-primary">
          {g.league}
        </div>
        {isLive ? (
          <div className="flex items-center gap-1 text-xs font-semibold text-red-500 mt-1">
            <span className="w-1.5 h-1.5 rounded-full bg-red-500 motion-safe:animate-pulse" />
            LIVE {g.period ?? ""}
          </div>
        ) : isFinal ? (
          <div className="text-xs text-muted-foreground mt-1">FINAL</div>
        ) : g.status === "postponed" || g.status === "cancelled" || g.status === "canceled" ? (
          <div className="text-xs text-muted-foreground mt-1 uppercase">{g.status}</div>
        ) : (
          <div className="text-xs text-muted-foreground mt-1">
            {new Date(g.startTime).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}
          </div>
        )}
      </div>
      <div className="flex-1 min-w-0 space-y-1">
        <div className="flex justify-between text-sm">
          <span className="truncate pr-2">{g.away.name}</span>
          <span className="font-bold tabular-nums">{g.awayScore ?? "—"}</span>
        </div>
        <div className="flex justify-between text-sm">
          <span className="truncate pr-2">{g.home.name}</span>
          <span className="font-bold tabular-nums">{g.homeScore ?? "—"}</span>
        </div>
        {g.venue && <p className="text-xs text-muted-foreground truncate">{g.venue}</p>}
      </div>
    </div>
  );
}

function Section({ title, games }: { title: string; games: ScoreEvent[] }) {
  if (games.length === 0) return null;
  return (
    <section>
      <h2 className="text-sm font-semibold uppercase tracking-wider mb-3">{title}</h2>
      <div className="grid gap-3">
        {games.map((g) => (
          <GameRow key={g.id} g={g} />
        ))}
      </div>
    </section>
  );
}

const Scores = () => {
  const [league, setLeague] = useState("all");
  const { data, isLoading, error } = useScoreboard();

  const leagues = data?.leagues ?? [];
  const all = data?.events ?? [];
  const games = league === "all" ? all : all.filter((g) => g.leagueCode === league);
  const { live, today, upcoming, recent } = bucketEvents(games);

  const lastUpdated = data?.lastUpdatedAt
    ? new Date(data.lastUpdatedAt).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })
    : null;

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main className="container py-12">
        <header className="mb-8">
          <span className="text-xs font-semibold uppercase tracking-[0.2em] text-primary">
            Live feed
          </span>
          <h1 className="text-5xl md:text-6xl mt-2 text-balance">Scores.</h1>
          <p className="text-muted-foreground mt-3 max-w-xl">
            Live and recent results across women's pro sports.
            {lastUpdated && ` Last updated ${lastUpdated}.`}
          </p>
        </header>

        {leagues.length > 1 && (
          <div className="flex flex-wrap gap-2 mb-8">
            {[{ league_code: "all", display_name: "All" }, ...leagues].map((l) => (
              <button
                key={l.league_code}
                onClick={() => setLeague(l.league_code)}
                className={`min-h-[44px] px-4 rounded-full text-sm font-semibold border transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
                  league === l.league_code
                    ? "bg-secondary text-secondary-foreground border-secondary"
                    : "bg-background text-foreground border-border hover:border-secondary"
                }`}
              >
                {l.display_name}
              </button>
            ))}
          </div>
        )}

        {error && (
          <div className="rounded-xl border border-destructive/30 bg-destructive/10 p-4 text-sm">
            Couldn't load scores right now. Please try again shortly.
          </div>
        )}

        {isLoading ? (
          <div className="grid gap-3">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="h-[88px] rounded-xl bg-muted motion-safe:animate-pulse" />
            ))}
          </div>
        ) : (
          <div className="space-y-10">
            <Section title="Live now" games={live} />
            <Section title="Today" games={today} />
            <Section title="Upcoming" games={upcoming} />
            <Section title="Recent results" games={recent} />
            {games.length === 0 && (
              <p className="text-muted-foreground py-12 text-center">
                No games on the slate. Check back soon.
              </p>
            )}
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
};

export default Scores;
